import { FormControl, NG_ASYNC_VALIDATORS } from '@angular/forms';
import { Directive, EventEmitter, HostBinding, Input, OnDestroy, Output } from '@angular/core';
import { finalize, map, Observable, of, Subscription } from 'rxjs';
import { UserService } from '@reg/shared/service';

@Directive({
    selector: '[floAsyncUserCoreEmailCheckValidator]',
    providers: [
        { provide: NG_ASYNC_VALIDATORS, useExisting: UserCoreEmailCheckDirective, multi: true },
    ],
})
export class UserCoreEmailCheckDirective implements OnDestroy {
    @Output() public isValidatingChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public userCoreExistsChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() public invalidIfExists: boolean = true;

    private _isValidating: boolean = false;
    @HostBinding('class.is-validating')
    set isValidating(isValidating: boolean) {
        this._isValidating = isValidating;
        this.isValidatingChange.emit(isValidating);
    }

    get isValidating(): boolean {
        return this._isValidating;
    }

    private formControl: FormControl;
    private timeout: any;
    private defaultDelay: number = 600; // wait 600 milliseconds before validating so we do not validate on every key stroke
    private delay: number = this.defaultDelay;
    private valueChangedSubscription: Subscription;

    constructor(private userService: UserService) {
    }

    public validate(control: FormControl): Observable<any> {
        clearTimeout(this.timeout);
        this.isValidating = true;
        this.formControl = control;

        if (!control.value) {
            this.userCoreExistsChange.emit(null);
            this.isValidating = false;
            return of(null);
        }

        // Set a timeout so we don't make an http call for every key stroke.
        return new Observable((obs: any): void => {
            this.timeout = setTimeout(
                () => {
                    this.delay = this.defaultDelay;

                    this.userService.getCheckCoreUserByEmail(control.value)
                        .pipe(
                            map((exists: boolean) => {
                                if (!exists &&
                                    !this.invalidIfExists) {
                                    // No user found and the component is flagged error if we can't find one
                                    return { emailNotFound: true };
                                }

                                if (!exists ||
                                    !control.value) {
                                    // No user and no value so return null (valid)
                                    this.userCoreExistsChange.emit(null);
                                    return null;
                                }

                                this.userCoreExistsChange.emit(exists);

                                if (exists &&
                                    this.invalidIfExists) {
                                    // Found a user and the component is flagged to error if we found one
                                    return { emailAlreadyExists: true };
                                }

                                return null;
                            }),
                            finalize(() => {
                                obs.complete();
                                this.isValidating = false;
                            }),
                        ).subscribe({
                            next: (data: any) => { obs.next(data); },
                            error: (error: any) => { obs.next({ invalid: true }); },
                        });
                },
                this.delay,
            );
        });
    }

    public ngOnDestroy(): void {
        if (this.valueChangedSubscription) {
            this.valueChangedSubscription.unsubscribe();
        }
    }
}
